import { useEffect, useRef, useCallback } from "react";
import { toast } from "sonner";

const POLL_INTERVAL_MS = 30_000;
const REQUEST_TIMEOUT_MS = 8_000;
const TOAST_ID = "connection-lost";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";
const HEALTH_URL = `${BACKEND_URL}/api/health`;

/**
 * Polls /api/health every 30 s via raw fetch (bypassing the axios interceptor
 * so a network error never triggers the 401-redirect path).
 *
 * Shows a persistent sonner toast when the backend is unreachable and
 * auto-dismisses it as soon as connectivity is restored.
 *
 * @param {boolean} enabled – pass false to pause polling (e.g. on the login
 *   page where no session is active and the form already surfaces errors).
 */
export function useHealthCheck({ enabled = true } = {}) {
  // Track whether we are currently showing the "connection lost" banner
  // so we only show/dismiss the toast on state transitions.
  const lostRef = useRef(false);

  const check = useCallback(async () => {
    const controller = new AbortController();
    const timer = setTimeout(() => controller.abort(), REQUEST_TIMEOUT_MS);
    try {
      const res = await fetch(HEALTH_URL, {
        signal: controller.signal,
        credentials: "include",
        cache: "no-store",
      });
      clearTimeout(timer);

      if (!res.ok) {
        // Treat a non-ok response (e.g. 503 overload) the same as a network error.
        throw new Error(`Health check failed with status ${res.status}`);
      }

      if (lostRef.current) {
        // Connection restored
        lostRef.current = false;
        toast.dismiss(TOAST_ID);
        toast.success("Connection restored", { id: "connection-restored", duration: 3000 });
      }
    } catch {
      clearTimeout(timer);
      if (!lostRef.current) {
        // Transition: connected → lost
        lostRef.current = true;
        toast.error("Connection lost — reconnecting\u2026", {
          id: TOAST_ID,
          duration: Infinity,
        });
      }
    }
  }, []);

  useEffect(() => {
    if (!enabled) {
      // If polling is disabled while a banner is showing, clean it up.
      if (lostRef.current) {
        lostRef.current = false;
        toast.dismiss(TOAST_ID);
      }
      return;
    }

    // Run immediately on mount so we surface a pre-existing outage right away
    check();
    const interval = setInterval(check, POLL_INTERVAL_MS);
    return () => {
      clearInterval(interval);
      // Clean up any visible banner when the component unmounts
      toast.dismiss(TOAST_ID);
    };
  }, [enabled, check]);
}
